import { Component, PropsWithChildren } from 'react';
import { withRouter } from 'react-router-dom';
import { User } from '../../../../../../typings/User.interface';
import Sentry from '../../../client/Sentry';
import { showChat } from '../../../utils/liveChat';
import { withUserContext } from '../../contexts/UserContext';
import Alert from '../base/Alert';
import Button from '../base/Button';
import Container from '../base/Container';
import Text from '../base/Text';
import { Div } from './StyledUtils';

class ErrorBoundary extends Component<
  PropsWithChildren<{ user?: User; location: any }>,
  { error?: Error }
> {
  constructor(props) {
    super(props);
    this.state = { error: undefined };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname && this.state.error) {
      this.setState({ error: undefined });
    }
  }

  componentDidCatch(error, errorInfo) {
    Sentry.withScope((scope) => {
      if (this.props.user) {
        scope.setUser(this.props.user);
      }
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key]);
      });

      Sentry.captureException(error);
    });
    this.setState({ error });
  }

  render() {
    return this.state.error ? (
      <Container medium p={{ y: 6 }}>
        <Text bold size="l" m={{ b: 2 }}>
          Something went wrong...
        </Text>
        <Text as="p" muted m={{ b: 4 }}>
          The technical team was notified and will look into it!
        </Text>
        {this.state.error.message && (
          <Alert danger inline m={{ b: 4 }}>
            {this.state.error.message}
          </Alert>
        )}
        <Div flex={{ align: 'center', gap: 3 }}>
          <Button primary onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button outline onClick={showChat}>
            Get help
          </Button>
        </Div>
      </Container>
    ) : (
      this.props.children
    );
  }
}

export default withRouter(withUserContext(['user'])(ErrorBoundary));
